// client/src/components/configuracoes/integracoes/ComprarCreditoModal.js
import React, { useState } from 'react';
import { CreditCard, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import FormModal from '../../layout/FormModal';
import { useAuth } from '../../../context/auth/AuthProvider';
import { usePacotesCredito, useComprarCredito } from '../../../hooks/useComunicacaoCredito';

// ✅ Cartão cru enviado direto pro backend (server/services/sinalPaymentService.js
// ::criarCobrancaAvulsa) — mesmo padrão já usado em SubscriptionPage.js
// (nenhum lugar do projeto usa tokenização client-side do Pagar.me). Sem
// endereço de cobrança aqui — comprarPacote() não exige um, diferente do
// fluxo de assinatura.
const ComprarCreditoModal = ({ onClose }) => {
    const { user } = useAuth();
    const { data: pacotes, isLoading: isLoadingPacotes } = usePacotesCredito();
    const comprar = useComprarCredito();

    const [pacoteId, setPacoteId] = useState('');
    const [cardData, setCardData] = useState({ number: '', holder_name: '', exp_month: '', exp_year: '', cvv: '' });
    const [cliente, setCliente] = useState({ nome: user?.name || '', email: user?.email || '' });
    const [erro, setErro] = useState('');

    const handleCardInputChange = (e) => {
        let { name, value } = e.target;
        if (name === 'number') {
            const digits = value.replace(/\D/g, '').substring(0, 16);
            value = digits.replace(/(\d{4})/g, '$1 ').trim();
        } else if (['exp_month', 'exp_year', 'cvv'].includes(name)) {
            value = value.replace(/\D/g, '');
        } else if (name === 'holder_name') {
            value = value.replace(/[^A-Za-zÀ-ÿ\s]/g, '');
        }
        setCardData((prev) => ({ ...prev, [name]: value }));
    };

    const handleClienteChange = (e) => {
        const { name, value } = e.target;
        setCliente((prev) => ({ ...prev, [name]: value }));
    };

    const formatarPreco = (centavos) => (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const handleSubmit = (e) => {
        e.preventDefault();
        setErro('');

        if (!pacoteId) return setErro('Selecione um pacote de créditos.');

        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const expMonth = parseInt(cardData.exp_month, 10);
        let expYear = parseInt(cardData.exp_year, 10);
        if (isNaN(expMonth) || expMonth < 1 || expMonth > 12) {
            return setErro('Mês de validade do cartão é inválido (use 01-12).');
        }
        if (expYear < 100) expYear += 2000;
        if (isNaN(expYear) || expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
            return setErro('Data de validade do cartão está no passado.');
        }
        if (cardData.cvv.length < 3 || cardData.cvv.length > 4) {
            return setErro('CVV inválido.');
        }
        if (!cliente.nome.trim() || !cliente.email.trim()) {
            return setErro('Nome e e-mail são obrigatórios.');
        }

        comprar.mutate(
            { pacoteId, card: { ...cardData, number: cardData.number.replace(/\s/g, '') }, cliente },
            { onSuccess: onClose }
        );
    };

    return (
        <FormModal
            title={<div className="flex items-center gap-2"><CreditCard size={20} /><span>Comprar Créditos de Comunicação</span></div>}
            onClose={onClose}
            size="large"
        >
            <div className="payment-form-content">
                {erro && (
                    <div className="error-message mb-4">
                        <AlertCircle size={20} />
                        <span>{erro}</span>
                    </div>
                )}
                {comprar.data?.data?.status === 'pendente' && (
                    <div className="alert alert-success mb-4" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <CheckCircle2 size={16} />
                        Pagamento em processamento — os créditos serão adicionados assim que confirmado.
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <h4 className="form-section-title">Pacote de Créditos</h4>
                        {isLoadingPacotes ? (
                            <div className="table-loading-state"><Loader2 size={20} className="animate-spin" /><span>Carregando pacotes...</span></div>
                        ) : (
                            <div className="form-grid-container-cols-2">
                                {(pacotes || []).map((p) => (
                                    <label
                                        key={p._id}
                                        className="form-group"
                                        style={{
                                            border: '1px solid var(--color-border, #e5e7eb)',
                                            borderRadius: '8px',
                                            padding: '0.75rem',
                                            cursor: 'pointer',
                                            background: pacoteId === p._id ? 'var(--color-primary-light, #eff6ff)' : 'transparent'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <input type="radio" name="pacoteId" value={p._id} checked={pacoteId === p._id} onChange={(e) => setPacoteId(e.target.value)} />
                                            <div>
                                                <strong>{p.nome}</strong>
                                                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                                                    {p.quantidadeCreditos} créditos — {formatarPreco(p.preco)}
                                                </div>
                                            </div>
                                        </div>
                                    </label>
                                ))}
                                {!isLoadingPacotes && (pacotes || []).length === 0 && (
                                    <p className="text-muted">Nenhum pacote disponível no momento.</p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Dados do Pagador</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label className="form-label required">Nome</label>
                                <input name="nome" value={cliente.nome} onChange={handleClienteChange} className="form-input" required disabled={comprar.isPending} />
                            </div>
                            <div className="form-group">
                                <label className="form-label required">E-mail</label>
                                <input type="email" name="email" value={cliente.email} onChange={handleClienteChange} className="form-input" required disabled={comprar.isPending} />
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h4 className="form-section-title">Dados do Cartão</h4>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group full-width">
                                <label className="form-label required">Nome no Cartão</label>
                                <input type="text" name="holder_name" value={cardData.holder_name} onChange={handleCardInputChange} className="form-input" required placeholder="Como escrito no cartão" disabled={comprar.isPending} />
                            </div>
                            <div className="form-group full-width">
                                <label className="form-label required">Número do Cartão</label>
                                <input type="text" name="number" value={cardData.number} onChange={handleCardInputChange} className="form-input" required placeholder="0000 0000 0000 0000" disabled={comprar.isPending} />
                            </div>
                            <div className="form-group">
                                <label className="form-label required">Mês (MM)</label>
                                <input type="text" name="exp_month" value={cardData.exp_month} onChange={handleCardInputChange} className="form-input" placeholder="MM" required maxLength="2" disabled={comprar.isPending} />
                            </div>
                            <div className="form-group">
                                <label className="form-label required">Ano (AA ou AAAA)</label>
                                <input type="text" name="exp_year" value={cardData.exp_year} onChange={handleCardInputChange} className="form-input" placeholder="AA ou AAAA" required maxLength="4" disabled={comprar.isPending} />
                            </div>
                            <div className="form-group">
                                <label className="form-label required">CVV</label>
                                <input type="text" name="cvv" value={cardData.cvv} onChange={handleCardInputChange} className="form-input" required maxLength="4" disabled={comprar.isPending} />
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="button" className="action-button action-button-secondary" onClick={onClose} disabled={comprar.isPending}>
                            Cancelar
                        </button>
                        <button type="submit" className="action-button action-button-primary" disabled={comprar.isPending || !pacoteId}>
                            {comprar.isPending ? <Loader2 size={18} className="icon animate-spin" /> : <CreditCard size={18} className="icon" />}
                            Comprar
                        </button>
                    </div>
                </form>
            </div>
        </FormModal>
    );
};

export default ComprarCreditoModal;
